import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AirportAutocomplete } from './AirportAutocomplete';
import { FlightPriceCalendar } from './FlightPriceCalendar';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PlaneTakeoff, PlaneLanding, ArrowLeftRight, Users, Search, Calendar as CalIcon, Plus, X } from 'lucide-react';

const CABINS = [
  { v: 'economy', l: 'Economy' }, { v: 'premium_economy', l: 'Premium Economy' },
  { v: 'business', l: 'Business' }, { v: 'first', l: 'First' },
];

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export const FlightSearchBar = ({ compact = false, initial = {}, onSearch }) => {
  const navigate = useNavigate();
  const [tripType, setTripType] = useState(initial.trip_type || 'round_trip');
  const [from, setFrom] = useState(initial.from || null);
  const [to, setTo] = useState(initial.to || null);
  const [depart, setDepart] = useState(initial.depart_date || todayPlus(14));
  const [ret, setRet] = useState(initial.return_date || todayPlus(21));
  const [adults, setAdults] = useState(initial.adults || 1);
  const [children, setChildren] = useState(initial.children || 0);
  const [cabin, setCabin] = useState(initial.cabin || 'economy');
  const [error, setError] = useState('');
  // Multi-city segments (each: {from, to, date}). Seeded from the current from/to on first switch.
  const [segments, setSegments] = useState([
    { from: null, to: null, date: todayPlus(14) },
    { from: null, to: null, date: todayPlus(21) },
  ]);

  const swap = () => { setFrom(to); setTo(from); };

  const setSeg = (i, patch) => setSegments((s) => s.map((seg, idx) => (idx === i ? { ...seg, ...patch } : seg)));
  const addSeg = () => setSegments((s) => (s.length >= 6 ? s : [...s, { from: s[s.length - 1]?.to || null, to: null, date: todayPlus(28) }]));
  const removeSeg = (i) => setSegments((s) => (s.length <= 2 ? s : s.filter((_, idx) => idx !== i)));

  const goResults = (extraParams) => {
    const q = new URLSearchParams(extraParams).toString();
    navigate(`/flights/results?${q}`);
  };

  const submit = () => {
    if (tripType === 'multi_city') {
      for (let i = 0; i < segments.length; i++) {
        const sg = segments[i];
        if (!sg.from || !sg.to) { setError(`Please select cities for flight ${i + 1}.`); return; }
        if (sg.from.code === sg.to.code) { setError(`Flight ${i + 1}: origin and destination cannot be the same.`); return; }
        if (!sg.date) { setError(`Please pick a date for flight ${i + 1}.`); return; }
      }
      setError('');
      const directions = segments.map((sg) => ({ origin: sg.from.code, destination: sg.to.code, date: sg.date }));
      const payload = { trip_type: 'multi_city', directions, adults: Number(adults), children: Number(children), cabin };
      if (onSearch) { onSearch(payload); return; }
      goResults({ trip_type: 'multi_city', mc: JSON.stringify(directions), adults, children, cabin });
      return;
    }
    if (!from || !to) { setError('Please select both origin and destination.'); return; }
    if (from.code === to.code) { setError('Origin and destination cannot be the same.'); return; }
    if (tripType === 'round_trip' && new Date(ret) < new Date(depart)) {
      setError('Return date cannot be before the departure date.'); return;
    }
    setError('');
    const payload = {
      origin: from.code, destination: to.code, trip_type: tripType,
      depart_date: depart, return_date: tripType === 'round_trip' ? ret : null,
      adults: Number(adults), children: Number(children), cabin,
      fromCity: from.city, toCity: to.city,
    };
    if (onSearch) { onSearch(payload); return; }
    goResults({
      origin: payload.origin, destination: payload.destination, trip_type: tripType,
      depart_date: depart, return_date: payload.return_date || '', adults, children, cabin,
    });
  };

  const Travellers = (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Travellers & cabin</label>
      <Popover>
        <PopoverTrigger asChild>
          <button className="w-full flex items-center gap-2 rounded-xl border border-input bg-card px-3 h-12 text-sm font-medium" data-testid="travellers-trigger">
            <Users className="h-4 w-4 text-accent" /> {adults + children} pax · {CABINS.find((c) => c.v === cabin)?.l}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-4 space-y-3">
          {[['Adults', adults, setAdults, 1], ['Children', children, setChildren, 0]].map(([l, val, set, min]) => (
            <div key={l} className="flex items-center justify-between">
              <span className="text-sm font-medium">{l}</span>
              <div className="flex items-center gap-3">
                <button onClick={() => set(Math.max(min, val - 1))} className="h-7 w-7 rounded-full border border-border" data-testid={`dec-${l.toLowerCase()}`}>−</button>
                <span className="w-5 text-center tabular">{val}</span>
                <button onClick={() => set(val + 1)} className="h-7 w-7 rounded-full border border-border" data-testid={`inc-${l.toLowerCase()}`}>+</button>
              </div>
            </div>
          ))}
          <div>
            <span className="text-sm font-medium block mb-1">Cabin class</span>
            <Select value={cabin} onValueChange={setCabin}>
              <SelectTrigger data-testid="cabin-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CABINS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <div className="rounded-2xl bg-card text-foreground border border-border p-4 sm:p-5 card-elevate" data-testid="home-flight-search">
      <div className="flex items-center gap-2 mb-4" data-testid="trip-type-toggle">
        {[['round_trip', 'Round trip'], ['one_way', 'One way'], ['multi_city', 'Multi-city']].map(([t, l]) => (
          <button key={t} onClick={() => setTripType(t)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${tripType === t ? 'bg-accent/12 text-accent border border-accent/30' : 'text-muted-foreground hover:bg-secondary border border-transparent'}`}
            data-testid={`trip-type-${t}`}>
            {l}
          </button>
        ))}
      </div>

      {tripType === 'multi_city' ? (
        <div className="space-y-3" data-testid="multicity-segments">
          {segments.map((sg, i) => (
            <div key={i} className="grid gap-3 md:grid-cols-12 items-end" data-testid={`multicity-segment-${i}`}>
              <div className="md:col-span-4 relative">
                <AirportAutocomplete label={`Flight ${i + 1} — From`} placeholder="City or airport" value={sg.from} onChange={(v) => setSeg(i, { from: v })} testid={`mc-from-${i}`} icon={PlaneTakeoff} />
              </div>
              <div className="md:col-span-4 relative">
                <AirportAutocomplete label="To" placeholder="City or airport" value={sg.to} onChange={(v) => setSeg(i, { to: v })} testid={`mc-to-${i}`} icon={PlaneLanding} />
              </div>
              <div className="md:col-span-3">
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Date</label>
                <div className="flex items-center gap-2 rounded-xl border border-input bg-card px-3 h-12">
                  <CalIcon className="h-4 w-4 text-accent" />
                  <input type="date" value={sg.date} min={todayPlus(0)} onChange={(e) => setSeg(i, { date: e.target.value })} className="w-full bg-transparent outline-none text-sm font-medium" data-testid={`mc-date-${i}`} />
                </div>
              </div>
              <div className="md:col-span-1 flex md:justify-center">
                {segments.length > 2 && (
                  <button onClick={() => removeSeg(i)} title="Remove flight" className="h-10 w-10 grid place-items-center rounded-xl border border-border hover:bg-secondary text-muted-foreground" data-testid={`mc-remove-${i}`}>
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={addSeg} disabled={segments.length >= 6} className="rounded-xl" data-testid="mc-add-segment">
              <Plus className="h-4 w-4 mr-1.5" /> Add another flight
            </Button>
            <div className="w-full sm:w-64">{Travellers}</div>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-12 items-end">
          <div className="md:col-span-3 relative">
            <AirportAutocomplete label="From" placeholder="City or airport" value={from} onChange={setFrom} testid="flight-search-from-input" icon={PlaneTakeoff} />
          </div>
          <div className="md:col-span-3 relative">
            <AirportAutocomplete label="To" placeholder="City or airport" value={to} onChange={setTo} testid="flight-search-to-input" icon={PlaneLanding} />
            <button onClick={swap} title="Swap" className="hidden md:grid place-items-center absolute -left-5 top-7 h-9 w-9 rounded-full bg-card border border-border shadow-sm hover:bg-secondary z-10" data-testid="swap-airports">
              <ArrowLeftRight className="h-4 w-4 text-accent" />
            </button>
          </div>

          <div className="md:col-span-2">
            <FlightPriceCalendar label="Depart" value={depart} testid="flight-search-depart-date"
              minDate={todayPlus(0)} origin={from?.code} destination={to?.code}
              roundTrip={tripType === 'round_trip'}
              tripDays={Math.max(1, Math.round((new Date(ret) - new Date(depart)) / 86400000)) || 7}
              onChange={(v) => { setDepart(v); if (tripType === 'round_trip' && ret && new Date(ret) < new Date(v)) setRet(v); }} />
          </div>
          <div className="md:col-span-2">
            <FlightPriceCalendar label="Return" value={ret} testid="flight-search-return-date"
              minDate={depart} origin={from?.code} destination={to?.code}
              roundTrip disabled={tripType === 'one_way'}
              tripDays={Math.max(1, Math.round((new Date(ret) - new Date(depart)) / 86400000)) || 7}
              onChange={(v) => setRet(v)} />
          </div>

          <div className="md:col-span-2">{Travellers}</div>
        </div>
      )}

      {error && <p className="mt-3 text-sm text-destructive" data-testid="search-error">{error}</p>}

      <div className="mt-4">
        <Button onClick={submit} className="w-full md:w-auto md:px-10 h-12 bg-accent text-accent-foreground hover:bg-accent/90 text-base font-semibold rounded-xl" data-testid="home-search-submit">
          <Search className="h-5 w-5 mr-2" /> Search flights
        </Button>
      </div>
    </div>
  );
};
